import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Heart, Handshake, Wallet, UserPlus, Users, Tag, Ticket, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  fadeInUp,
  staggerContainer,
  fastStaggerContainer,
  scaleIn,
  hoverScale,
  defaultTransition,
} from '../utils/animations';

interface StatisticsData {
  id: number;
  medical_letters: number;
  probono: number;
  sponsors: number;
  '230': number;
  navicare: number;
  oamenibuni: number;
  'nr-formulare-230': number;
}

export default function Statistics() {
  const [statistics, setStatistics] = useState<StatisticsData | null>(null);
  const [doctorsCount, setDoctorsCount] = useState<number>(0);
  const [patientsCount, setPatientsCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('Supabase URL for debugging:', import.meta.env.VITE_SUPABASE_URL);
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      // Fetch statistics table data
      const { data: statsData, error: statsError } = await supabase
        .from('statistics')
        .select('*');

      if (statsError) throw statsError;
      console.log('Fetched all statistics data:', statsData);
      
      // Sort by id descending and take the first (latest) row
      const sortedData = statsData ? [...statsData].sort((a, b) => b.id - a.id) : [];
      const statisticsData = sortedData.length > 0 ? sortedData[0] : null;
      
      console.log('Sorted data (descending by id):', sortedData);
      console.log('Using latest statisticsData:', statisticsData);
      setStatistics(statisticsData);

      // Fetch doctors count
      const { data: doctorsData, error: doctorsError } = await supabase
        .from('profiles')
        .select('role')
        .eq('role', 'doctor');

      if (doctorsError) throw doctorsError;
      const totalDoctors = doctorsData?.length || 0;
      const adjustedDoctors = Math.max(0, totalDoctors - 3); // Subtract 3 test accounts
      console.log('Doctors count:', totalDoctors, 'Adjusted:', adjustedDoctors);
      setDoctorsCount(adjustedDoctors);

      // Fetch patients count
      const { data: patientsData, error: patientsError } = await supabase
        .from('profiles')
        .select('role')
        .eq('role', 'pacient');

      if (patientsError) throw patientsError;
      const totalPatients = patientsData?.length || 0;
      const adjustedPatients = Math.max(0, totalPatients - 1); // Subtract 1 test account
      console.log('Patients count:', totalPatients, 'Adjusted:', adjustedPatients);
      setPatientsCount(adjustedPatients);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError(
        error instanceof Error ? error.message : 'Error fetching statistics'
      );
    } finally {
      setLoading(false);
    }
  };

  const statisticCards = [
    {
      id: 1,
      title: 'Consultații efectuate',
      value: statistics?.medical_letters || 0,
      icon: Calendar,
      color: 'from-cyan-400 to-blue-500',
      description: 'Numărul total de consultații medicale efectuate',
    },
    {
      id: 2,
      title: 'Consultații gratuite',
      value: statistics?.probono || 0,
      icon: Heart,
      color: 'from-purple-400 to-pink-500',
      description: 'Numărul de consultații oferite gratuit',
    },
    {
      id: 3,
      title: 'Medici',
      value: doctorsCount,
      icon: UserPlus,
      color: 'from-blue-400 to-indigo-500',
      description: 'Numărul de medici activi',
    },
    {
      id: 4,
      title: 'Pacienți',
      value: patientsCount,
      icon: Users,
      color: 'from-emerald-400 to-green-500',
      description: 'Numărul de pacienți cu conturi active',
    },
    {
      id: 5,
      title: 'Sponsorizări',
      value: statistics?.sponsors || 0,
      icon: Handshake,
      color: 'from-green-400 to-teal-500',
      description: 'Valorea totală a sponsorizărilor primite',
      suffix: ' lei',
    },
    {
      id: 6,
      title: 'Redirecționare impozit',
      value: statistics?.['230'] || 0,
      icon: Wallet,
      color: 'from-orange-400 to-red-500',
      description: 'Valoarea totală redirecționată prin formularul 230 în 2025',
      suffix: ' lei',
    },
    {
      id: 7,
      title: 'Navicare',
      value: statistics?.navicare || 0,
      icon: Tag,
      color: 'from-violet-400 to-purple-500',
      description: 'Pacienți care au fost recomandați de Navicare',
    },
    {
      id: 8,
      title: 'Oameni Buni',
      value: statistics?.oamenibuni || 0,
      icon: Ticket,
      color: 'from-amber-400 to-yellow-500',
      description: 'Pacienți care au fost recomandați de Asociația Oameni Buni',
    },
    {
      id: 9,
      title: 'Nr. 230',
      value: statistics?.['nr-formulare-230'] || 0,
      icon: FileText,
      color: 'from-rose-400 to-pink-500',
      description: 'Numărul total de formulare din anul curent',
    },
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#024994] via-[#012a52] to-[#0A0E27] text-white overflow-hidden">
      {/* Animated Background Elements - Similar to Hero.tsx */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Orbs */}
        <motion.div
          className="absolute top-20 left-10 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl"
          animate={{
            y: [-20, 20, -20],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-32 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"
          animate={{
            y: [100, -50, 100],
            transition: {
              duration: 16,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/3 w-72 h-72 bg-purple-500/8 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-64 h-64 bg-cyan-400/15 rounded-full blur-3xl"
          animate={{
            y: [-15, 15, -15],
            x: [-10, 10, -10],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

        {/* Particle Effect */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/40 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.1, 1, 0.1],
              transition: {
                duration: 4 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 4,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header Section */}
        <motion.div
          className="text-center mb-16"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-white mb-6"
            variants={fadeInUp}
          >
            Statistici
          </motion.h1>
          <motion.p
            className="text-xl text-[#D3D1CF] max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            Impactul nostru în numere: o privire de ansamblu asupra activității
            noastre și a rezultatelor obținute pentru comunitate.
          </motion.p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
            <p className="mt-4 text-[#D3D1CF]">Se încarcă statisticile...</p>
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <motion.div
            className="bg-red-500/20 border border-red-500/50 rounded-xl p-6 max-w-2xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-red-200">
              A apărut o eroare la încărcarea statisticilor:{' '}
              <span className="font-semibold">{error}</span>
            </p>
            <button
              onClick={fetchStatistics}
              className="mt-4 bg-red-500/30 hover:bg-red-500/40 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Reîncarcă
            </button>
          </motion.div>
        )}

        {/* Statistics Grid */}
        {!loading && !error && (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-50px' }}
            variants={fastStaggerContainer}
          >
            {statisticCards.map((card) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.id}
                  className="relative group"
                  variants={scaleIn}
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${card.color}/10 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />
                  <motion.div
                    className="relative bg-white/10 backdrop-blur-lg p-8 rounded-xl border border-white/20 hover:border-cyan-400/50 transition-colors duration-300 h-full"
                    whileHover={{ y: -8 }}
                    transition={defaultTransition}
                  >
                    {/* Icon Container */}
                    <div
                      className={`bg-gradient-to-br ${card.color} w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto`}
                    >
                      <Icon className="w-10 h-10 text-white" />
                    </div>

                    {/* Number Counter */}
                    <motion.div
                      className="text-5xl font-bold text-center mb-4"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        delay: card.id * 0.1,
                        duration: 0.5,
                        ease: 'easeOut',
                      }}
                    >
                      {card.value.toLocaleString()}
                      {card.suffix || ''}
                    </motion.div>

                    {/* Title */}
                    <h3 className="text-xl font-semibold text-center mb-2">
                      {card.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[#D3D1CF] text-center text-sm">
                      {card.description}
                    </p>

                    {/* Decorative Element */}
                    <div className="mt-6 flex justify-center">
                      <div
                        className={`h-1 w-16 rounded-full bg-gradient-to-r ${card.color}`}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Additional Info Section */}
        {!loading && !error && (
          <motion.div
            className="mt-20 text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <motion.div
              className="relative bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10 max-w-4xl mx-auto"
              variants={fadeInUp}
            >
              <motion.h2
                className="text-2xl font-bold mb-4"
                variants={fadeInUp}
              >
                Despre aceste statistici
              </motion.h2>
              <motion.p
                className="text-[#D3D1CF] leading-relaxed mb-6"
                variants={fadeInUp}
              >
                Aceste numere reflectă impactul real al eforturilor noastre în
                comunitate. Fiecare cifră reprezintă o viață atinsă, o
                consultație oferită sau o resursă direcționată către cei care au
                nevoie. Ne străduim să oferim transparență completă și să
                răspundem încrederii pe care o acordați proiectului nostru.
              </motion.p>
            </motion.div>
          </motion.div>
        )}

        {/* Refresh Button */}
        {!loading && (
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <motion.button
              onClick={fetchStatistics}
              className="relative bg-white/10 backdrop-blur-sm text-white px-8 py-3 rounded-full text-lg font-semibold overflow-hidden group border border-white/20"
              {...hoverScale}
            >
              <span className="relative z-10">Actualizează statisticile</span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
